import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import NewBadge from '@/vdp/Badges/NewBadge';
import { Separator } from '@/vdp/VehicleInfo/Terms/Terms.styles';
import { useIsDesktop, useIsMobile } from '~/context/common/DeviceContext';
import { useStickyBanner } from '~/context/vdp/StickyBannerContext';
import {
  useGetVehicleDetails,
  useIsNewVehicle
} from '~/context/vdp/VehicleContext';
import useShouldShowMiniHeroDesktop from '~/hooks/vdp/useShouldShowMiniHeroDesktop';
import Price from './Price';
import UnavailableHeroContent from './UnavailableHeroContent';
import Badge from '../Badge';

type OwnProps = { showBadge?: boolean };

export default function HeroContent({ showBadge = true }: OwnProps) {
  const {
    make = '',
    mileage = 0,
    model = '',
    trim = '',
    year = 0
  } = useGetVehicleDetails();
  const isDesktop = useIsDesktop();
  const shouldShowMiniHeroDesktop = useShouldShowMiniHeroDesktop();
  const isMobile = useIsMobile();
  const isNewVehicle = useIsNewVehicle();
  const { setShouldShowStickyBanner } = useStickyBanner();

  const showUnavailableContent = isDesktop && !shouldShowMiniHeroDesktop;

  const [ref, inView] = useInView({
    threshold: [0, 1],
    initialInView: true // Assume in view initially to prevent flash
  });

  useEffect(() => {
    if (isMobile) {
      setShouldShowStickyBanner(!inView);
    }
  }, [inView, isMobile, setShouldShowStickyBanner]);

  if (showUnavailableContent) {
    return (
      <div className="flex flex-col gap-md">
        <UnavailableHeroContent />
      </div>
    );
  }

  if (shouldShowMiniHeroDesktop) {
    return (
      <div className="flex flex-col gap-lg">
        {isNewVehicle && (
          <div className="flex">
            <NewBadge />
          </div>
        )}
        <div className="flex flex-col gap-sm">
          <h1 className="text-text-strong t-heading-lg lg:t-heading-2xl">
            {year} {make} {model}
          </h1>
          <div className="flex flex-wrap items-center gap-x-sm text-text-strong t-body-sm">
            <span>{trim}</span>
            <Separator />
            <span>{mileage.toLocaleString()} miles</span>
          </div>
        </div>
        <div className="text-text-strong t-heading-lg lg:t-heading-2xl">
          <Price />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-md">
      {showBadge && <Badge />}
      <div className="text-text-strong t-heading-sm">
        <h1 className="t-heading-sm">
          {year} {make} {model}
        </h1>
        <div className="mb-sm text-text-weak t-body-sm">{trim}</div>
        <div ref={ref}>
          <Price />
        </div>
      </div>
    </div>
  );
}
